import React, {useState, useEffect, useCallback} from 'react';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';
import _ from 'lodash';
import {Link, useLocation, useSearchParams} from 'react-router-dom';
import classnames from 'classnames/bind';
import actions from '../../redux/actions';
import style from './index.module.scss';
import {
  ICountry,
  ILang,
  IState,
  Replacement,
} from '../../interface';
import ClickOut from 'react-click-out';
import localesReplacement from '../../utils/localesReplacement';
import isoCountry from 'i18n-iso-countries';
import ReactCountryFlag from 'react-country-flag';
import useURLParameter from '../../hooks/use-url-parameter';
import {apiInstance} from '../../api/requestInterceptor';
import UndirectLink from '../UndirectLink';
import LandingLink from '../LandingLink';
import { APPNAME, FASTSCAN } from '../../constants';
import { useAppName } from '../../hooks/use-app-names';

const cx = classnames.bind(style);

interface IProps {
  lang: string;
  langs: ILang[];
  country: ICountry;
  countries: ICountry[];
  replacements: Replacement;
  setLang: (value: string) => void;
  setCountry: (value: ICountry) => void;
  hasCastrolCertifiedWorkshops: boolean;
}

//Page header component
function Header(props: IProps) {
  const {
    lang,
    langs,
    country,
    countries,
    replacements,
    setLang,
    setCountry,
  } = props;
  const isFastScan = useAppName(FASTSCAN);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [menu, setMenu] = useState<boolean>(false);
  const [visiable, setVisible] = useState<boolean>(false);
  const [langsVisiable, setLangsVisible] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const {languageIsoCode, countryIsoCode} = useURLParameter(
    location.pathname,
    countries,
    langs,
  );

  useEffect(() => {
    if (
      (countryIsoCode &&
        !_.isEqual(
          _.get(apiInstance.defaults.headers, 'countryCode'),
          countryIsoCode,
        )) ||
      (languageIsoCode &&
        !_.isEqual(
          _.get(apiInstance.defaults.headers, 'languageCode'),
          languageIsoCode,
        ))
    ) {
      window.location.reload();
    }
  }, [languageIsoCode, countryIsoCode]);

  //Disable scroll when modal or flyout appear on mobile view
  useEffect(() => {
    const body = document.body;
    window.scrollTo(0, 0);
    menu ? body.classList.add('no-scroll') : body.classList.remove('no-scroll');
  }, [menu]);

  

  const selectCountry = (value: ICountry) => {
    setCountry(value);
    setVisible(false);
    window.location.reload();
    localStorage.setItem('countryCode', value.isoCode);
  };
  const selectLang = (value: ILang) => {
    setLang(value.isoCode);
    setLangsVisible(false);
    window.location.reload();
    localStorage.setItem('languageCode', value.isoCode);
  };

  const wtbClick = useCallback(() => {
    searchParams.set('page', 'wheretobuy');
    isFastScan && searchParams.set(APPNAME, FASTSCAN);
    setSearchParams(searchParams);
  }, []);

  return (
    <div className={cx('main-content')}>
      <header>
        {/* menu*/}
        <div className={cx('menu')}>
          <span
            role='button'
            className={`button ${cx('icon-menu')}`}
            onClick={() => setMenu(!menu)}
          ></span>
        </div>
        <div className={cx('header-placeholder')}></div>
        {/** main navigation */}
        <div className={cx('header')}>
          <h1>{localesReplacement(replacements, 'Castrol')}</h1>
          <nav>
            <ul>
              {country.name && !countryIsoCode && (
                <li className={cx('location')}>
                  <ClickOut
                    className='click-out'
                    onClickOut={() => setVisible(false)}
                  >
                    <div className={cx('country-selection')}>
                      <div
                        className={cx('selection')}
                        onClick={() => setVisible(!visiable)}
                      >
                        <i>
                          <ReactCountryFlag
                            style={{
                              display: 'block',
                              width: '24px',
                              height: '16px',
                            }}
                            svg
                            countryCode={isoCountry.alpha3ToAlpha2(
                              country.isoCode,
                            )}
                          />
                        </i>
                        <span className={cx('text')}>
                          {_.chain(countries)
                            .find(['isoCode', country.isoCode])
                            .get('name')
                            .value()}
                        </span>
                      </div>
                      {visiable && (
                        <div className={cx('flyout')}>
                          <div className={cx('filter')}>
                            <input
                              value={searchText}
                              onChange={(evt) =>
                                setSearchText(evt.target.value)
                              }
                            />
                          </div>
                          <ul>
                            {_.map(
                              _.filter(countries, (item) =>
                                new RegExp(searchText, 'i').test(
                                  _.toString(item.name),
                                ),
                              ),
                              (c, i) => (
                                <li
                                  key={i}
                                  onClick={() => {
                                    selectCountry(c);
                                  }}
                                  className={cx({
                                    actived: _.isEqual(
                                      c['isoCode'],
                                      country.isoCode,
                                    ),
                                  })}
                                >
                                  <i>
                                    <ReactCountryFlag
                                      style={{
                                        display: 'block',
                                        width: '24px',
                                        height: '16px',
                                      }}
                                      svg
                                      countryCode={isoCountry.alpha3ToAlpha2(
                                        c.isoCode,
                                      )}
                                    />
                                  </i>
                                  <span className={cx('text')}>
                                    {c['name']}
                                  </span>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </ClickOut>
                </li>
              )}
              <li className={cx('ftro')}>
                <LandingLink>
                  {localesReplacement(replacements, 'FIND THE RIGHT OIL')}
                </LandingLink>
              </li>
              <li className={cx('shop')}>
                <UndirectLink>
                  <span className={cx('text')}>
                    {localesReplacement(replacements, 'SHOP ONLINE')}
                  </span>
                </UndirectLink>
              </li>
              <li className={cx('wtb')}>
                <Link to='#' onClick={wtbClick}>
                  {localesReplacement(replacements, 'WHERE TO BUY')}
                </Link>
              </li>
              <li className={cx('contact')}>
                <UndirectLink>
                  {localesReplacement(replacements, 'CONTACT')}
                </UndirectLink>
              </li>
              {languageIsoCode ? null : (
                <li>
                  <ClickOut
                    className={cx('lang')}
                    onClickOut={() => {
                      setLangsVisible(false);
                    }}
                  >
                    <UndirectLink
                      onClick={() => setLangsVisible(!langsVisiable)}
                    >
                      {localesReplacement(
                        replacements,
                        _.find(langs, ['isoCode', lang])?.englishName as string,
                      )}
                    </UndirectLink>
                    {langsVisiable && (
                      <div className={cx('flyout')}>
                        <ul>
                          {_.map(langs, (item, index) => (
                            <li key={index} onClick={() => selectLang(item)}>
                              {item.englishName}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </ClickOut>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </header>
      {/** sub navigation on mobile view */}
      <ul className={cx('nav-link')}>
        <li className={cx('ftro')}>
          <LandingLink>
            {localesReplacement(replacements, 'FIND THE RIGHT OIL')}
          </LandingLink>
        </li>
        <li className={cx('wtb')}>
          <Link to='#' onClick={wtbClick}>
            {localesReplacement(replacements, 'WHERE TO BUY')}
          </Link>
        </li>
      </ul>
      {/** sub navigation */}
      <div className={cx('secondary-header', {open: menu})}>
        <div className={cx('search-auth')}>
          <UndirectLink className={cx('custom-area')}>
            {localesReplacement(replacements, 'CUSTOMER AREA')}
          </UndirectLink>
          <span className={`button ${cx('search')}`} role='button'></span>
        </div>
        <nav>
          <ul>
            <li>
              <UndirectLink>
                {localesReplacement(replacements, 'SUSTAINABILITY')}
              </UndirectLink>
            </li>
            <li>
              <UndirectLink>
                {localesReplacement(replacements, 'CASTROL IN SPACE')}
              </UndirectLink>
            </li>
            <li>
              <UndirectLink>
                {localesReplacement(replacements, 'OUR STORY')}
              </UndirectLink>
            </li>
            <li>
              <UndirectLink>
                {localesReplacement(replacements, 'OILS AND FLUIDS')}
              </UndirectLink>
            </li>
            <li>
              <UndirectLink>
                {localesReplacement(replacements, 'INDUSTRIAL')}{' '}
              </UndirectLink>
            </li>
            <li>
              <UndirectLink>
                {localesReplacement(replacements, 'MARINE')}
              </UndirectLink>
            </li>
            <li>
              <UndirectLink>
                {localesReplacement(
                  replacements,
                  'AUTOMOTIVE REPAIR WORKSHOPS',
                )}
              </UndirectLink>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}

const mapStateToProps = (state: IState) => ({
  lang: state.lang,
  langs: state.langs,
  country: state.country,
  countries: state.countries,
  replacements: state.replacements,
  hasCastrolCertifiedWorkshops: state.hasCastrolCertifiedWorkshops,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setLang: (lang: string) => dispatch(actions.setLang(lang)),
  setCountry: (country: ICountry) => dispatch(actions.setCountry(country)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
